package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/*
 * Moderation wire types, mirroring openapi.yaml v1.0.0 tags `Moderation` and `Admin` -- ONE report
 * operation and EIGHT admin ones, and nothing else:
 *
 *     POST   /reports                            createReport
 *     GET    /admin/reports                      adminListReports
 *     GET    /admin/reports/{report_id}          adminGetReport
 *     POST   /admin/reports/{report_id}/resolve  adminResolveReport
 *     POST   /admin/users/{user_id}/block        adminBlockUser
 *     DELETE /admin/users/{user_id}/block        adminUnblockUser
 *     DELETE /admin/discover/posts/{post_id}     adminDeleteDiscoverPost
 *     DELETE /admin/discover/comments/{comment_id}  adminDeleteDiscoverComment
 *     DELETE /admin/messages/{message_id}        adminDeleteMessage
 *
 * Five of the nine answer 204 and have no body type at all, and one -- `adminBlockUser` -- takes an
 * INLINE body the contract declares nowhere in `components/schemas`. It still gets a named type
 * here ([AdminBlockUserRequest]), because a client cannot send an anonymous schema.
 *
 *
 * THE ONE ASYMMETRY THAT WILL LOOK LIKE A MISTAKE, AND IS NOT
 *
 * `CreateReportRequest.target_type` and `.reason` are ENUMS in the contract. `Report.target_type`
 * and `Report.reason` are bare `type: string` with NO enum. That is what v1.0.0 says, it is frozen,
 * and the types below mirror it exactly: enums going IN, plain strings coming OUT. Tightening the
 * response side to a Kotlin enum would be stricter than the contract and would turn a value a later
 * server learned to store into a decode failure on an old client, which is the opposite of what a
 * frozen contract is for. [ReportTargetTypes] and [ReportReasons] carry the known values as
 * constants so the string side is still comparable without anybody re-typing a literal.
 *
 * `Report.status` DOES carry an enum in the contract, so it is an enum here.
 *
 *
 * DEFAULTS, AND THE ONE RULE THAT DECIDES THEM
 *
 * Same rule `Discover.kt` states: a contract-REQUIRED property gets NO Kotlin default, because
 * `coerceInputValues` substitutes a property's declared default and a property without one fails to
 * decode rather than quietly becoming something the server never sent. Optional properties keep
 * defaults so a response that omits them decodes.
 *
 * [ResolveReportRequest.action] is the interesting one and is documented on the property.
 */

// ---------------------------------------------------------------------------------- constants

/**
 * `CreateReportRequest.target_type`, and the values `Report.target_type` can carry on the way back.
 *
 * Strings rather than an enum on the response side, for the reason the file header gives. These
 * constants exist so that `report.targetType == ReportTargetTypes.DISCOVER_POST` is possible
 * without a literal, and so the five values appear in exactly one place.
 */
object ReportTargetTypes {
    const val USER = "user"
    const val MESSAGE = "message"
    const val DISCOVER_POST = "discover_post"
    const val DISCOVER_COMMENT = "discover_comment"
    const val GROUP = "group"
    const val PICSO_POST = "picso_post"

    val ALL = listOf(USER, MESSAGE, DISCOVER_POST, DISCOVER_COMMENT, GROUP, PICSO_POST)
}

/**
 * `CreateReportRequest.reason`, and the values `Report.reason` can carry back.
 *
 * SPS §16.3 lists the same seven in prose and the contract's spellings win where they differ:
 * "hate or abusive content" is `hate_abuse` and "illegal content" is `illegal_content`.
 */
object ReportReasons {
    const val SPAM = "spam"
    const val SCAM = "scam"
    const val HARASSMENT = "harassment"
    const val HATE_ABUSE = "hate_abuse"
    const val IMPERSONATION = "impersonation"
    const val ILLEGAL_CONTENT = "illegal_content"
    const val OTHER = "other"

    val ALL = listOf(SPAM, SCAM, HARASSMENT, HATE_ABUSE, IMPERSONATION, ILLEGAL_CONTENT, OTHER)
}

/** Constraints the contract states for the moderation bodies. */
object ModerationLimits {
    /** `CreateReportRequest.details`: nullable string, maxLength 1000. */
    const val DETAILS_MAX = 1000

    /** `ResolveReportRequest.note`: nullable string, maxLength 1000. */
    const val NOTE_MAX = 1000

    /** The `reason` on `adminBlockUser`'s inline body: required string, maxLength 500. */
    const val BLOCK_REASON_MAX = 500

    /** `Limit` on `adminListReports`: minimum 1, maximum 100, default 30. */
    const val PAGE_LIMIT_MIN = 1
    const val PAGE_LIMIT_MAX = 100
    const val PAGE_LIMIT_DEFAULT = 30
}

/**
 * Error codes this surface produces that `ErrorCodes` does not yet carry.
 *
 * `report_target_not_found` is the ONE answer `createReport` gives for every way a target can fail
 * to resolve -- no such id, an id in a group the reporter is not in, a post whose author they have
 * blocked, a message from before they joined. They are deliberately indistinguishable: a reporting
 * endpoint that said "that post exists but you cannot see it" would be a live existence oracle for
 * private content, reachable by anybody with an account and a list of guessed uuids.
 *
 * IT BELONGS IN [ErrorCodes] AND SHOULD MOVE THERE when `Errors.kt` is next edited; it is declared
 * separately only so this change adds a moderation code without touching the shared catalog. The
 * duplication risk is nil -- it is one constant with one producer -- and `ERRORS.md` documents it
 * in the Moderation table beside `report_not_found` and `admin_role_required`, which already exist
 * in [ErrorCodes] and are used unchanged.
 */
object ModerationErrorCodes {
    /** 404. The reported target does not exist, or the reporter cannot see it. */
    const val REPORT_TARGET_NOT_FOUND = "report_target_not_found"
}

// ---------------------------------------------------------------------------------- enums

/**
 * `CreateReportRequest.target_type`, REQUIRED, enum of five.
 *
 * NO DEFAULT. This property decides which table the server resolves `target_id` against, and a
 * coerced value would either resolve nothing (a 404 for a report the user really did file about
 * something real) or -- far worse if the resolution were ever made lenient -- resolve the same uuid
 * in the wrong space. The contract is frozen and this enum is closed at five, so there is no later
 * value to be compatible with and a default buys nothing.
 *
 * [UNKNOWN] exists because every wire enum in this module has one and because it keeps `when`
 * blocks exhaustive. Under `coerceInputValues` a property with no default throws rather than
 * coercing, so decoding never produces it; only the literal string `"unknown"` does.
 */
@Serializable
enum class ReportTargetType {
    @SerialName("user") USER,
    @SerialName("message") MESSAGE,
    @SerialName("discover_post") DISCOVER_POST,
    @SerialName("discover_comment") DISCOVER_COMMENT,
    @SerialName("group") GROUP,
    @SerialName("picso_post") PICSO_POST,
    @SerialName("unknown") UNKNOWN,
}

/** `CreateReportRequest.reason`, REQUIRED, enum of seven. No default, for [ReportTargetType]'s reason. */
@Serializable
enum class ReportReason {
    @SerialName("spam") SPAM,
    @SerialName("scam") SCAM,
    @SerialName("harassment") HARASSMENT,
    @SerialName("hate_abuse") HATE_ABUSE,
    @SerialName("impersonation") IMPERSONATION,
    @SerialName("illegal_content") ILLEGAL_CONTENT,
    @SerialName("other") OTHER,
    @SerialName("unknown") UNKNOWN,
}

/**
 * `Report.status`, REQUIRED, enum of four -- and the query filter on `adminListReports`.
 *
 * [REVIEWING] is in the contract and NOTHING WRITES IT. No v1.0.0 operation claims a report:
 * `adminGetReport` is a GET and must not have side effects, and `adminResolveReport` moves straight
 * to a terminal state. It is here because the contract declares it and because a moderator can set
 * it by hand; a client must render it rather than treat it as impossible.
 */
@Serializable
enum class ReportStatus {
    @SerialName("open") OPEN,
    @SerialName("reviewing") REVIEWING,
    @SerialName("resolved") RESOLVED,
    @SerialName("rejected") REJECTED,
    @SerialName("unknown") UNKNOWN,
}

/**
 * `ResolveReportRequest.decision`, REQUIRED, enum `resolve | reject`.
 *
 * NO KOTLIN DEFAULT, exactly as `ResolveGroupJoinRequestRequest.decision` has none: there is no safe
 * value to fall back to. Defaulting to `resolve` closes a real complaint nobody read; defaulting to
 * `reject` throws one away. The server maps [UNKNOWN] to `400 validation_failed` on the field, the
 * same shape `joinRequestStatusFor` produces, so the refusal names what was wrong instead of
 * arriving as a bare decode failure.
 */
@Serializable
enum class ReportDecision {
    @SerialName("resolve") RESOLVE,
    @SerialName("reject") REJECT,
    @SerialName("unknown") UNKNOWN,
}

/**
 * `ResolveReportRequest.action`, OPTIONAL, enum `none | delete_content | block_user`.
 *
 * The side effect the moderator wants along with the decision. `delete_content` destroys the
 * reported post, comment or message; `block_user` suspends the account behind it.
 *
 * IT IS NULLABLE HERE AND THE CONTRACT'S ENUM IS NOT, and that is the whole design of this
 * property. The field is optional, so an omitted `action` has to decode -- which under
 * `coerceInputValues` means it needs a Kotlin default, which in turn means an UNRECOGNISED value
 * also lands on that default. Omission and garbage therefore cannot be told apart, and the only
 * question left is WHICH WAY THAT FAILS.
 *
 * It fails toward doing nothing. `null` means [NONE], so a value the server could not read never
 * deletes a post and never suspends an account: the moderator sees the content still there, and
 * tries again. The other arrangement -- treating an unreadable action as destructive -- would
 * destroy somebody's words because of a typo, and there is no undo. `Discover.kt` argues the
 * general rule (a coerced default is indistinguishable from a real value on screen); this is that
 * rule with the tie broken by which mistake can be taken back.
 */
@Serializable
enum class ReportAction {
    @SerialName("none") NONE,
    @SerialName("delete_content") DELETE_CONTENT,
    @SerialName("block_user") BLOCK_USER,
    @SerialName("unknown") UNKNOWN,
}

// ---------------------------------------------------------------------------------- reports

/**
 * `CreateReportRequest` -- the `createReport` body.
 *
 * NO `client_id` AND NO `Idempotency-Key`. The operation declares neither, unlike `sendMessage` and
 * `createGroup`, so the usual idempotency machinery has nothing to hang on. Retry safety comes from
 * the storage rule instead: while a reporter's earlier report about the same target is still open,
 * a repeat returns THAT report rather than filing a second one. See `V10__moderation.sql`.
 */
@Serializable
data class CreateReportRequest(
    @SerialName("target_type") val targetType: ReportTargetType,
    @SerialName("target_id") val targetId: Uuid,
    @SerialName("reason") val reason: ReportReason,
    /** Free text from the reporter, maxLength 1000. Optional, so it keeps a default. */
    @SerialName("details") val details: String? = null,
)

/**
 * `Report` -- the `createReport` 201 body, the `adminGetReport` 200 body, and the item type of
 * [ReportPage].
 *
 * ONE TYPE FOR TWO AUDIENCES, because the contract declares one. The reporter sees it exactly once,
 * as the receipt for their own filing, where every field is either their own input or a fact about
 * their own report. Moderators see it from `/admin`. Nothing here is a secret from the reporter
 * about themselves, and there is no operation that would ever hand one reporter another's row.
 *
 * WHAT IT DOES NOT CARRY, and cannot: the reported CONTENT. `Report` has `target_type` and
 * `target_id` and no shape for a title, a preview or an author, so an admin panel must fetch the
 * content through the ordinary read operations -- which succeed only if the moderator can see it.
 * For a post in a private group they have not joined, that fetch answers 404. STA §16.1 asks for
 * "view reported user/post/message metadata" and v1.0.0 provides no operation that could serve it;
 * recorded as a known gap rather than worked around.
 *
 * [targetType] and [reason] are STRINGS, not enums -- see the file header. [ReportTargetTypes] and
 * [ReportReasons] carry the known values.
 */
@Serializable
data class Report(
    @SerialName("id") val id: Uuid,
    @SerialName("target_type") val targetType: String,
    @SerialName("target_id") val targetId: Uuid,
    @SerialName("reason") val reason: String,
    @SerialName("status") val status: ReportStatus,
    @SerialName("created_at") val createdAt: Timestamp,
    /** `oneOf: [Uuid, null]`. NULL once the reporter's account has been anonymized. */
    @SerialName("reporter_id") val reporterId: Uuid? = null,
    @SerialName("details") val details: String? = null,
    /**
     * Lowercase hex SHA-256 of the reported text at the moment the report was FILED, or null for a
     * `user` or `group` target, which has no single piece of content. It is not the content and
     * cannot be reversed into it; it exists so a moderator can tell whether the thing they are
     * about to delete is the thing that was reported, and whether two reports name the same text --
     * both of which stay answerable after a hard delete. STA §16.3, STA §31.4.
     */
    @SerialName("content_hash") val contentHash: String? = null,
    /** The moderator who decided this report. Null while it is open. */
    @SerialName("assigned_to") val assignedTo: Uuid? = null,
    @SerialName("resolved_at") val resolvedAt: Timestamp? = null,
)

/**
 * `ReportPage` -- the `adminListReports` 200 body.
 *
 * `items` gets NO default, matching the Discover pages and unlike `MessagePage`: a malformed page
 * is a decode failure, not an empty queue. A moderation queue that renders "nothing to review"
 * because a response was not understood is the single worst way this surface can fail.
 */
@Serializable
data class ReportPage(
    @SerialName("items") val items: List<Report>,
    @SerialName("page_info") val pageInfo: PageInfo,
)

/**
 * `ResolveReportRequest` -- the `adminResolveReport` body.
 *
 * [note] is accepted by the contract and readable by NOTHING in it: `Report` has no property for a
 * note, so no v1.0.0 operation can render one back. The server stores it anyway rather than
 * discarding a moderator's words silently. See `V10__moderation.sql` and DECISIONS §19.
 */
@Serializable
data class ResolveReportRequest(
    @SerialName("decision") val decision: ReportDecision,
    /** Optional and NULLABLE; null means [ReportAction.NONE]. See [ReportAction]. */
    @SerialName("action") val action: ReportAction? = null,
    /** Moderator's note, maxLength 1000. Stored, never rendered back under v1.0.0. */
    @SerialName("note") val note: String? = null,
)

/**
 * The `adminBlockUser` body.
 *
 * THE CONTRACT DECLARES THIS INLINE, under the operation, with no entry in `components/schemas` and
 * therefore no name of its own -- the only body in the moderation surface shaped that way. The name
 * here is this codebase's; the SHAPE is the contract's, `{ "reason": string }` with `reason`
 * required and maxLength 500.
 *
 * `reason` is required by the contract and that is right: a suspension nobody can review, including
 * the moderator who imposed it, is one that cannot be lifted with any confidence.
 */
@Serializable
data class AdminBlockUserRequest(
    @SerialName("reason") val reason: String,
)
